import ReconciliationHistory from '../../../Banking/reconciliation/ReconciliationHistory';
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { format, parseISO, isAfter } from "date-fns";
import { sv } from "date-fns/locale";
import { Spinner, Alert, OverlayTrigger, Tooltip, Dropdown } from "react-bootstrap";
import axios from "../../../../../services/AxiosInstance";
import { getApiUrl } from "../../../../../utils/ApiUtils";
import PageHeader from "../../../../layouts/PageHeader";
import InvoiceNotesList from "../Notes/InvoiceNotesList";
import EmailModal from "../EmailModal";
import RegisterPaymentModal from "../RegisterPaymentModal";
import VerificationDetailsModal from "../../../Accounting/Verification/VerificationDetailsModal";
import Swal from "sweetalert2";
import { toast } from "sonner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getStatusColor, INVOICE_STATUSES } from '../../../../../config/invoiceStatus';
import { Receipt, ExternalLink, Undo2, Mail, Info, Banknote, Download, Bell, Ban, FileMinus, Settings, Check, ChevronRight } from 'lucide-react';
import StatusBadge from '../../../Common/StatusBadge';
import { Section, Facts, Fact } from '../../Personell/EmployeeDetailParts';
import './invoice-detail.css';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useAccountingSettings } from '../../../../../context/AccountingSettingsContext';
import {
  faFileInvoice,
  faBan,
  faCreditCard,
  faCheckCircle,
  faClock,
  faReceipt,
  faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';

const InvoiceDetailBootstrap = () => {
  const history = useHistory();
  const { companyId, invoiceId } = useParams();
  const { accountingSettings } = useAccountingSettings();
  const [invoice, setInvoice] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPaid, setTotalPaid] = useState(0);
  const [combinedTimeline, setCombinedTimeline] = useState([]);
  const invoiceRef = useRef(null);
  const [customerData, setCustomerData] = useState(null);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Constants for document types
  const DocumentType = {
    INVOICE: 'invoice',
    CREDIT_NOTE: 'credit_note'
  };

  // Alias for convenience (imported from config/invoiceStatus)
  const StatusType = INVOICE_STATUSES;

  // Fetch all invoice data including verifications
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Fetch invoice details first to get customer ID
        const invoiceResponse = await axios.get(
          getApiUrl(`${companyId}/invoices/${invoiceId}/details`),
          { withCredentials: true }
        );

        const invoiceData = invoiceResponse.data.data;
        const customerId = invoiceData.customerid;

        // Now fetch all data in parallel including customer details and verifications
        const [statusHistoryResponse, paymentsResponse, customerResponse, verificationsResponse] = await Promise.all([
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/status-history`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch status history:", error);
              return { data: { data: [] } };
            }),
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/payments`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch payments:", error);
              return { data: { data: [] } };
            }),
          customerId ?
            axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}`), { withCredentials: true })
              .catch(error => {
                console.warn("Failed to fetch customer details:", error);
                return { data: null };
              })
            : Promise.resolve({ data: null }),
          // Fetch verifications for this invoice
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/verifications`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch verifications:", error);
              return { data: { data: [] } };
            })
        ]);

        // Extract data
        const statusHistoryData = statusHistoryResponse.data.data || [];
        const paymentsData = paymentsResponse.data.data || [];
        const customerData = customerResponse?.data;
        const verificationsData = verificationsResponse.data.data || [];

        console.log("Invoice data:", invoiceData);
        console.log("Customer data:", customerData);
        console.log("Status history:", statusHistoryData);
        console.log("Payments:", paymentsData);
        console.log("Verifications:", verificationsData);

        // Set state with fetched data
        setInvoice(invoiceData);
        setStatusHistory(statusHistoryData);
        setPaymentHistory(paymentsData);
        setCustomerData(customerData);
        setVerifications(verificationsData);
        setTotalPaid(paymentsData.reduce((sum, p) => sum + parseFloat(p.amount_paid), 0));

        // Combine timeline events including verifications
        const combinedEvents = [];

        // Add status events
        statusHistoryData.forEach(status => {
          combinedEvents.push({
            date: parseISO(status.changed_at),
            type: 'status',
            status: status.status,
            change_type: status.change_type,
            displayName: status.status,
            icon: faCheckCircle
          });
        });

        // Add payment events
        paymentsData.forEach(payment => {
          combinedEvents.push({
            date: parseISO(payment.created_at || payment.payment_date),
            type: 'payment',
            amount: parseFloat(payment.amount_paid),
            verificationNumber: payment.verification_number,
            paymentMethod: payment.payment_method || 'Bank transfer',
            status: 'Payment',
            isPayment: true,
            icon: faCreditCard
          });
        });

        // Add verification events — include invoice, year-end, and annulment verifications.
        // Exclude payment/refund verifications since those are shown as payment events.
        verificationsData
          .filter(v => ['customer_invoice', 'credit_note_publication', 'customer_invoice_year_end', 'verification_reversal'].includes(v.reference_type))
          .forEach(verification => {
            combinedEvents.push({
              date: parseISO(verification.date),
              type: 'verification',
              verificationId: verification.verification_id,
              verificationNumber: `${verification.series}${verification.number}`,
              description: verification.description,
              referenceType: verification.reference_type,
              referenceStatus: verification.reference_status,
              status: 'Verification',
              isLocked: verification.is_locked,
              icon: faReceipt
            });
          });

        // Add credit note events
        if (invoiceData.credit_notes?.length > 0) {
          invoiceData.credit_notes.forEach(creditNote => {
            combinedEvents.push({
              date: parseISO(creditNote.created_at || creditNote.invoicedate || creditNote.date),
              type: 'credit_note',
              creditNoteId: creditNote.id || creditNote.credit_note_id,
              creditNoteNumber: creditNote.creditnote_number || creditNote.credit_note_number,
              amount: Math.abs(parseFloat(creditNote.total_sum || creditNote.amount || 0)) + Math.abs(parseFloat(creditNote.tax_amount || 0)),
              creditNoteStatus: creditNote.status,
              refundStatus: creditNote.refund_status,
              icon: faFileInvoice
            });
          });
        }

        // Sort by date (newest first)
        combinedEvents.sort((a, b) => b.date.getTime() - a.date.getTime());
        setCombinedTimeline(combinedEvents);

        console.log("Combined timeline:", combinedEvents);

        // Fetch PDF after everything else is loaded
        fetchPdf();
      } catch (error) {
        console.error("Error fetching invoice data:", error);
        setError("Kunde inte hämta fakturadetaljer. Vänligen försök igen senare.");
      } finally {
        setLoading(false);
      }
    }

    if (invoiceId && companyId) {
      fetchData();
    }
  }, [companyId, invoiceId]);

  const fetchPdf = async () => {
    try {
      const response = await axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/pdf`), {
        responseType: 'blob',
        withCredentials: true,
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setPdfData(fileURL);
    } catch (error) {
      console.error('Error fetching the PDF:', error);
    }
  };

  // Function to view verification details in modal (matches supplier invoice pattern)
  const handleViewVerification = async (verification) => {
    try {
      const verificationId = typeof verification === 'string' ? verification : verification.verification_id;
      const response = await axios.get(
        getApiUrl(`${companyId}/verification/${verificationId}`),
        { withCredentials: true }
      );
      const data = response.data;
      if (!data || !data.verification_id) return;

      const transactions = (data.transactions || []).map(tx => ({
        transaction_id: tx.transaction_id,
        account_id: tx.account_id,
        account_number: tx.account_number,
        account_name: tx.account_name,
        account_type: tx.account_type,
        amount: tx.amount,
        transaction_type: tx.transaction_type,
        description: tx.description
      }));

      let debitSum = 0;
      let creditSum = 0;
      transactions.forEach(tx => {
        if (tx.transaction_type === 'debit') debitSum += parseFloat(tx.amount);
        else if (tx.transaction_type === 'credit') creditSum += parseFloat(tx.amount);
      });

      setSelectedVerification({
        verification_id: data.verification_id,
        date: data.date,
        verification_description: data.description,
        series: data.series,
        number: data.number,
        created_at: data.created_at,
        reference: data.reference,
        notes: data.notes,
        transactions,
        debitSum,
        creditSum
      });
      setShowVerificationDetails(true);
    } catch (error) {
      console.error('Error fetching verification details:', error);
    }
  };

  const handleRegisterPayment = () => setShowPaymentModal(true);

  const handlePaymentRegistered = () => {
    setShowPaymentModal(false);
    const isRefund = invoice.invoice_type === 'credit_note';
    toast.success(isRefund ? 'Återbetalning registrerad!' : 'Betalning registrerad!', {
      description: isRefund
        ? 'Återbetalningen har bokförts (2820 → bank).'
        : 'Betalningen har bokförts på fakturan.',
      duration: 3000,
    });
    // Reload after a short delay so the toast is seen
    setTimeout(() => window.location.reload(), 1000);
  };

  const handleCreateCreditNote = () => {
    history.push(`/${companyId}/create-credit-note/${invoice.invoiceid || invoice.id}`);
  };

  const handleSendEmail = async (emailDetails) => {
    const response = await axios.post(
      getApiUrl(`${companyId}/invoices/${invoiceId}/send-email`),
      emailDetails,
      { withCredentials: true }
    );
    if (response.data.deliveryRecorded === false) {
      toast.warning('E-post skickad', { description: 'Fakturan skickades, men leveransstatus kunde inte sparas. Skicka inte igen.' });
    } else {
      toast.success('E-post skickad', { description: `Fakturan skickades till ${emailDetails.email}.` });
      setInvoice(current => ({ ...current, sent_at: new Date().toISOString() }));
    }
  };

  const handleSendReminder = () => {
    Swal.fire({
      title: 'Skicka påminnelse',
      text: `Vill du skicka en betalningspåminnelse för faktura #${invoice.invoicenumber}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ja, skicka påminnelse',
      cancelButtonText: 'Avbryt'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/send-reminder`),
          {},
          { withCredentials: true }
        )
          .then(() => {
            toast.success('Påminnelse skickad!', {
              description: 'Betalningspåminnelsen har skickats till kunden.'
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error sending reminder:", error);
            toast.error('Fel!', {
              description: 'Kunde inte skicka påminnelsen. Försök igen senare.'
            });
          });
      }
    });
  };

  const handleCancelInvoice = () => {
    Swal.fire({
      title: 'Makulera faktura',
      text: `Är du säker på att du vill makulera faktura #${invoice.invoicenumber}? Detta kan inte ångras.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, makulera faktura',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/cancel`),
          {},
          { withCredentials: true }
        )
          .then(() => {
            toast.success('Fakturan makulerad!', {
              description: 'Fakturan har makulerats och kan inte längre betalas.'
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error cancelling invoice:", error);
            toast.error('Fel!', {
              description: 'Kunde inte makulera fakturan. Försök igen senare.'
            });
          });
      }
    });
  };

  // Undo info map - maps status to undo impact description
  const getUndoInfo = (status) => {
    const infoMap = {
      [INVOICE_STATUSES.PARTIALLY_PAID]: {
        title: 'Ångra senaste betalning?',
        impacts: ['Annulleringsverifikat skapas', 'Betalningen markeras som omvänd'],
        level: 'warning'
      },
      [INVOICE_STATUSES.PAID]: {
        title: 'Ångra senaste betalning?',
        impacts: ['Annulleringsverifikat skapas', 'Betalningen markeras som omvänd', 'Fakturan återställs till föregående status'],
        level: 'warning'
      }
    };
    return infoMap[status] || null;
  };

  const handleUndoClick = () => {
    const undoInfo = getUndoInfo(invoice.status);
    if (!undoInfo) return;

    Swal.fire({
      title: undoInfo.title,
      html: `
        <div class="text-start">
          <p class="mb-2"><strong>Detta kommer att:</strong></p>
          <ul class="mb-0">
            ${undoInfo.impacts.map(i => `<li>${i}</li>`).join('')}
          </ul>
        </div>
      `,
      icon: undoInfo.level,
      showCancelButton: true,
      confirmButtonText: 'Ja, ångra',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        setUndoLoading(true);
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/undo`),
          {},
          { withCredentials: true }
        )
          .then((response) => {
            toast.success('Steget har ångrats.', {
              description: `Status ändrad till ${response.data?.status || 'föregående status'}`,
              duration: 3000
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error undoing step:", error);
            const message = error.response?.data?.message || 'Kunde inte ångra steget. Försök igen senare.';
            toast.error('Fel!', { description: message, duration: 5000 });
            setUndoLoading(false);
          });
      }
    });
  };

  // Helper function for status display names (Swedish translations)
  const getStatusDisplayName = (status, documentType = 'invoice') => {
    const statusNames = {
      [DocumentType.INVOICE]: {
        [StatusType.DRAFT]: 'Utkast',
        [StatusType.SENT]: 'Publicerad',
        [StatusType.PARTIALLY_PAID]: 'Delvis betald',
        [StatusType.PAID]: 'Betald',
        [StatusType.CANCELLED]: 'Makulerad',
        [StatusType.CREDITED]: 'Krediterad',
        'Reminder Sent': 'Påminnelse skickad'
      },
      [DocumentType.CREDIT_NOTE]: {
        [StatusType.DRAFT]: 'Utkast',
        [StatusType.SENT]: 'Publicerad',
        'Applied': 'Tillämpad på faktura',  // Legacy credit note status
        [StatusType.CREDITED]: 'Krediterad',
        [StatusType.CANCELLED]: 'Makulerad'
      }
    };

    return statusNames[documentType]?.[status] || status;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  // Get status color and icon (using centralized config for colors)
  const getStatusConfig = (status) => {
    const colorVariant = getStatusColor(status);

    // Map Bootstrap color variants to custom colors
    const colorMap = {
      'secondary': { color: '#6b7280', bg: '#f3f4f6' },
      'primary': { color: '#3b82f6', bg: '#eff6ff' },
      'warning': { color: '#f59e0b', bg: '#fffbeb' },  // Orange for Partially_Paid
      'success': { color: '#059669', bg: '#f0fdf4' },
      'danger': { color: '#dc2626', bg: '#fef2f2' },
      'info': { color: '#8b5cf6', bg: '#f5f3ff' }  // Purple for Credited
    };

    const colors = colorMap[colorVariant] || colorMap['secondary'];

    const configs = {
      [INVOICE_STATUSES.DRAFT]: { ...colors, icon: faClock },
      [INVOICE_STATUSES.SENT]: { ...colorMap['primary'], icon: faFileInvoice },
      [INVOICE_STATUSES.PARTIALLY_PAID]: { ...colorMap['warning'], icon: faHourglassHalf },
      [INVOICE_STATUSES.PAID]: { ...colorMap['success'], icon: faCheckCircle },
      [INVOICE_STATUSES.CANCELLED]: { ...colorMap['secondary'], icon: faBan },
      [INVOICE_STATUSES.CREDITED]: { ...colorMap['info'], icon: faReceipt }
    };

    return configs[status] || { color: '#6b7280', bg: '#f3f4f6', icon: faFileInvoice };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar fakturadetaljer...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Alert variant="danger" className="text-center">
          <h5>Ett fel uppstod</h5>
          <p>{error}</p>
          <Link
            to={`/${companyId}/invoices`}
            className="ll-btn ll-btn--secondary"
          >
            Tillbaka till fakturalistan
          </Link>
        </Alert>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div>
        <Alert variant="warning" className="text-center">
          <h5>Faktura hittades inte</h5>
          <p>Den begärda fakturan kunde inte hittas.</p>
          <Link
            to={`/${companyId}/invoices`}
            className="ll-btn ll-btn--secondary"
          >
            Tillbaka till fakturalistan
          </Link>
        </Alert>
      </div>
    );
  }

  // Calculate remaining amount and effective amount
  const totalCreditNoteAmount = (invoice.credit_notes || []).reduce(
    (sum, cn) => {
      const subtotal = Math.abs(parseFloat(cn.total_sum || cn.amount || 0));
      const tax = Math.abs(parseFloat(cn.tax_amount || 0));
      return sum + subtotal + tax;
    }, 0);

  const effectiveAmount = invoice.totals.grand_total - totalCreditNoteAmount;
  const remainingAmount = effectiveAmount - totalPaid;

  // Calculate days until due date
  const today = new Date();
  const dueDate = parseISO(invoice.deadline);
  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  const isCreditNote = invoice.invoice_type === 'credit_note';
  const isPaid = invoice.status === INVOICE_STATUSES.PAID;
  const isOverdue = !isPaid && daysUntilDue < 0;
  const customerName = customerData?.name || invoice.customername || 'Ingen kund';
  const docTitle = `${isCreditNote ? 'Kreditnota' : 'Faktura'} ${invoice.invoicenumber || ''}`.trim();
  const paidShare = effectiveAmount > 0 ? Math.min(100, Math.max(0, (totalPaid / effectiveAmount) * 100)) : 0;
  const fmtDate = (d) => format(typeof d === 'string' ? parseISO(d) : d, 'd MMM yyyy', { locale: sv });
  const fmtDateTime = (d) => format(d, 'd MMM yyyy, HH:mm', { locale: sv });

  const STATUS_TONES = {
    [INVOICE_STATUSES.DRAFT]: 'muted',
    [INVOICE_STATUSES.SENT]: 'info',
    [INVOICE_STATUSES.PARTIALLY_PAID]: 'warning',
    [INVOICE_STATUSES.PAID]: 'success',
    [INVOICE_STATUSES.CANCELLED]: 'muted',
    [INVOICE_STATUSES.CREDITED]: 'info',
  };

  const canRegisterPayment = !(
    invoice.status === INVOICE_STATUSES.CANCELLED ||
    invoice.status === INVOICE_STATUSES.DRAFT ||
    invoice.status === INVOICE_STATUSES.PAID ||
    invoice.status === INVOICE_STATUSES.CREDITED ||
    (isCreditNote && parseFloat(invoice.refund_amount || 0) === 0)
  );

  // Status steps. 6-state model: Draft → Sent → Partially_Paid → Paid, or the
  // terminal Credited / Cancelled in place of the last step.
  const statusMap = {};
  combinedTimeline.filter((e) => e.type === 'status').forEach((event) => {
    if (!event.change_type || event.change_type === 'forward') {
      statusMap[event.status] = event.date;
    }
  });
  const currentStatus = invoice.status;
  const steps = [
    { key: INVOICE_STATUSES.DRAFT, label: 'Utkast' },
    { key: INVOICE_STATUSES.SENT, label: 'Publicerad' },
  ];
  if (currentStatus === INVOICE_STATUSES.PARTIALLY_PAID) {
    steps.push({ key: currentStatus, label: 'Delvis betald' });
  } else if (currentStatus === INVOICE_STATUSES.CANCELLED) {
    steps.push({ key: currentStatus, label: 'Makulerad' });
  } else if (currentStatus === INVOICE_STATUSES.CREDITED) {
    steps.push({ key: currentStatus, label: 'Krediterad' });
  } else {
    steps.push({ key: INVOICE_STATUSES.PAID, label: 'Betald' });
  }
  const currentStepIndex = steps.findIndex((step) => step.key === currentStatus);
  const stepState = (index) => {
    if (statusMap[steps[index].key]) return 'completed';
    if (index === currentStepIndex) return 'current';
    return 'pending';
  };
  const canUndo = currentStepIndex > 0
    && [INVOICE_STATUSES.PARTIALLY_PAID, INVOICE_STATUSES.PAID].includes(currentStatus);

  const moneyEvents = combinedTimeline.filter((e) => e.type === 'payment' || e.type === 'credit_note');
  const creditNoteStateLabel = (event) => {
    if (event.creditNoteStatus === INVOICE_STATUSES.DRAFT) return 'Utkast';
    if (event.creditNoteStatus === INVOICE_STATUSES.SENT) return 'Publicerad';
    if (event.creditNoteStatus === INVOICE_STATUSES.CREDITED || event.refundStatus === 'refunded') return 'Krediterad';
    return 'Väntar på kreditering';
  };

  const hasActivePayments = paymentHistory && paymentHistory.some((p) => !p.reversed);
  const accrualLocked = !['Draft', 'Sent'].includes(invoice.status) || hasActivePayments;
  const toggleAccrual = async (e) => {
    e.stopPropagation();
    if (accrualLocked) return;
    try {
      const newValue = !invoice.use_accrual_method;
      await axios.put(
        getApiUrl(`${companyId}/invoices/${invoiceId}`),
        { use_accrual_method: newValue },
        { withCredentials: true }
      );
      toast.success(newValue ? 'Fakturan periodiseras nu (fakturametoden)' : 'Periodisering borttagen (kontantmetoden)');
      // Reload to show updated verifications
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      console.error('Error toggling accrual method:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || 'Kunde inte ändra bokföringsmetod.';
      toast.error(msg);
    }
  };

  return (
    <>
      <PageHeader
        backTo={{ to: `/${companyId}/invoices`, label: 'Alla kundfakturor' }}
        icon={Receipt}
        title={docTitle}
        subtitle={customerName}
        actions={
          <>
            <button
              type="button"
              className="ll-btn ll-btn--primary"
              onClick={handleRegisterPayment}
              disabled={!canRegisterPayment}
            >
              <Banknote />
              {isCreditNote && parseFloat(invoice.refund_amount || 0) > 0
                ? `Återbetala ${formatCurrency(parseFloat(invoice.refund_amount))}`
                : 'Registrera betalning'}
            </button>

            <Dropdown align="end">
              <Dropdown.Toggle as="button" type="button" className="ll-btn ll-btn--secondary" id="invoice-actions-dropdown">
                Fler åtgärder
              </Dropdown.Toggle>
              <Dropdown.Menu className="inv-detail__menu">
                {/* Credit note + reminder actions — only for regular invoices in active states */}
                {!isCreditNote &&
                 ![INVOICE_STATUSES.DRAFT, INVOICE_STATUSES.CANCELLED, INVOICE_STATUSES.CREDITED].includes(invoice.status) && (
                  <>
                    <Dropdown.Item onClick={() => setShowEmailModal(true)}>
                      <Mail size={15} />
                      {invoice.sent_at ? 'Skicka igen med e-post' : 'Skicka med e-post'}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={handleCreateCreditNote}>
                      <FileMinus size={15} />
                      Skapa kreditnota
                    </Dropdown.Item>
                    {new Date() > parseISO(invoice.deadline) && !isPaid && (
                      <Dropdown.Item onClick={handleSendReminder}>
                        <Bell size={15} />
                        Skicka påminnelse
                      </Dropdown.Item>
                    )}
                    <Dropdown.Divider />
                  </>
                )}

                <Dropdown.Item onClick={() => window.open(pdfData, '_blank')} disabled={!pdfData}>
                  <Download size={15} />
                  Ladda ner PDF
                </Dropdown.Item>

                {/* Undo action - available for Partially_Paid, Paid */}
                {[INVOICE_STATUSES.PARTIALLY_PAID, INVOICE_STATUSES.PAID].includes(invoice.status) && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleUndoClick}>
                      <Undo2 size={15} />
                      Ångra senaste steg
                    </Dropdown.Item>
                  </>
                )}

                {/* Cancel — not for terminal states */}
                {![INVOICE_STATUSES.CANCELLED, INVOICE_STATUSES.DRAFT, INVOICE_STATUSES.PAID, INVOICE_STATUSES.CREDITED].includes(invoice.status) && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleCancelInvoice} className="is-danger">
                      <Ban size={15} />
                      Makulera faktura
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>

            {/* Settings — periodisering toggle */}
            {(invoice.use_accrual_method || invoice.spans_fiscal_years) ? (
              <Dropdown align="end">
                <Dropdown.Toggle
                  as="button"
                  type="button"
                  className="ll-btn ll-btn--secondary inv-detail__icon-toggle"
                  id="invoice-settings-dropdown"
                  aria-label="Bokföringsinställningar"
                  title="Bokföringsinställningar"
                >
                  <Settings />
                </Dropdown.Toggle>
                <Dropdown.Menu className="inv-detail__menu" style={{ minWidth: '19rem' }}>
                  <Dropdown.Item
                    as="div"
                    role="switch"
                    aria-checked={Boolean(invoice.use_accrual_method)}
                    className={`inv-detail__switch-row${accrualLocked ? ' is-locked' : ''}`}
                    onClick={toggleAccrual}
                  >
                    <span className="d-flex align-items-center gap-2">
                      Bokför som kundfordran
                      <OverlayTrigger
                        placement="left"
                        overlay={
                          <Tooltip>
                            {accrualLocked
                              ? 'Kan inte ändras — fakturan har betalningar eller är avslutad'
                              : 'Bokför som kundfordran (konto 1510). Verifikation skapas på fakturadatum.'}
                          </Tooltip>
                        }
                      >
                        <Info size={13} style={{ cursor: 'help' }} />
                      </OverlayTrigger>
                    </span>
                    <span className={`inv-detail__switch${invoice.use_accrual_method ? ' is-on' : ''}`} />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </>
        }
      >
        <div className="inv-detail__meta">
          <StatusBadge tone={STATUS_TONES[invoice.status] || 'muted'}>
            {getStatusDisplayName(invoice.status, isCreditNote ? 'credit_note' : 'invoice')}
          </StatusBadge>
          <span>Fakturadatum <strong>{fmtDate(invoice.invoicedate)}</strong></span>
          <span className={isOverdue ? 'is-danger' : (!isPaid && daysUntilDue <= 7 ? 'is-warning' : undefined)}>
            Förfaller <strong>{fmtDate(invoice.deadline)}</strong>
            {isOverdue && ` · ${Math.abs(daysUntilDue)} d försenad`}
            {!isPaid && daysUntilDue === 0 && ' · idag'}
            {!isPaid && daysUntilDue > 0 && daysUntilDue <= 7 && ` · ${daysUntilDue} d kvar`}
          </span>
          {invoice.use_accrual_method ? (
            <OverlayTrigger placement="bottom" overlay={<Tooltip>Denna faktura periodiseras (fakturametoden)</Tooltip>}>
              <span><StatusBadge tone="info">Periodiserad</StatusBadge></span>
            </OverlayTrigger>
          ) : null}
        </div>
      </PageHeader>

      <div className="inv-detail">
        {/* The invoice itself */}
        <div className="inv-detail__main">
          <div className="inv-detail__doc">
            <div className="inv-detail__doc-bar">
              <span className="inv-detail__doc-name">{docTitle}</span>
              {pdfData && (
                <div className="inv-detail__doc-actions">
                  <a className="ll-btn ll-btn--ghost ll-btn--sm" href={pdfData} target="_blank" rel="noreferrer">
                    <ExternalLink />
                    Öppna
                  </a>
                  <a
                    className="ll-btn ll-btn--ghost ll-btn--sm"
                    href={pdfData}
                    download={`Faktura-${invoice.invoicenumber || 'utkast'}.pdf`}
                  >
                    <Download />
                    Ladda ner
                  </a>
                </div>
              )}
            </div>
            <div className="inv-detail__doc-frame">
              {pdfData ? (
                <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                  <Viewer fileUrl={pdfData} />
                </Worker>
              ) : (
                <div className="inv-detail__doc-loading">
                  <Spinner animation="border" size="sm" />
                  <span>Laddar dokument…</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="inv-detail__aside">
          <Section id="inv-payment" title="Betalning">
            <ReconciliationHistory companyId={companyId} invoiceId={invoiceId} onChanged={()=>window.location.reload()}/>
            <div className="inv-detail__amount">
              {formatCurrency(isPaid ? totalPaid : Math.max(remainingAmount, 0))}
            </div>
            <div className="inv-detail__amount-label">
              {isPaid ? 'Betalt i sin helhet' : isOverdue ? 'Kvar att betala · förfallen' : 'Kvar att betala'}
            </div>
            <div
              className="inv-detail__progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(paidShare)}
              aria-label="Andel betald"
            >
              <span style={{ width: `${paidShare}%` }} />
            </div>
            <Facts tight>
              <Fact label="Fakturabelopp">{formatCurrency(invoice.totals.grand_total)}</Fact>
              <Fact label="Betalt" tone={totalPaid > 0 ? 'success' : undefined}>{formatCurrency(totalPaid)}</Fact>
              {totalCreditNoteAmount > 0 && (
                <Fact label="Krediterat">−{formatCurrency(totalCreditNoteAmount)}</Fact>
              )}
            </Facts>

            {moneyEvents.length > 0 && (
              <>
                <div className="inv-detail__subhead">Händelser</div>
                <ul className="inv-detail__rows">
                  {moneyEvents.map((event, i) => {
                    const isCn = event.type === 'credit_note';
                    const body = (
                      <>
                        {isCn ? <FileMinus size={16} /> : <Banknote size={16} />}
                        <span className="inv-detail__row-main">
                          <span className="inv-detail__row-title d-block">
                            {isCn ? `Kreditnota ${event.creditNoteNumber || ''}` : 'Betalning'}
                          </span>
                          <span className="inv-detail__row-sub">
                            {fmtDateTime(event.date)}{isCn && ` · ${creditNoteStateLabel(event)}`}
                          </span>
                        </span>
                        <span className="inv-detail__row-amount">
                          {isCn ? '−' : ''}{formatCurrency(event.amount)}
                        </span>
                      </>
                    );
                    return (
                      <li key={i}>
                        {isCn ? (
                          <button
                            type="button"
                            className="inv-detail__row"
                            onClick={() => history.push(`/${companyId}/view-invoice/${event.creditNoteId}`)}
                          >
                            {body}
                          </button>
                        ) : (
                          <div className="inv-detail__row">{body}</div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </Section>

          <Section id="inv-status" title="Status">
            <ol className="inv-detail__steps">
              {steps.map((step, index) => {
                const state = stepState(index);
                const timestamp = statusMap[step.key];
                return (
                  <li key={step.key} className={`inv-detail__step is-${state}`}>
                    <span className="inv-detail__step-dot" aria-hidden="true">
                      {state === 'completed' && <Check size={13} strokeWidth={3} />}
                    </span>
                    <div>
                      <div className="inv-detail__step-label">{step.label}</div>
                      {timestamp && <div className="inv-detail__step-time">{fmtDateTime(timestamp)}</div>}
                      {index === currentStepIndex && canUndo && (
                        <button type="button" className="inv-detail__link" onClick={handleUndoClick} disabled={undoLoading}>
                          <Undo2 size={12} />
                          {undoLoading ? 'Ångrar…' : 'Ångra'}
                        </button>
                      )}
                      {index === currentStepIndex && currentStatus === INVOICE_STATUSES.SENT && !isCreditNote && (
                        <button type="button" className="inv-detail__link" onClick={handleCreateCreditNote}>
                          <FileMinus size={12} />
                          Kreditera faktura
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </Section>

          {((verifications && verifications.length > 0) || invoice.credit_notes?.length > 0) && (
            <Section id="inv-bookkeeping" title="Bokföring">
              {verifications && verifications.length > 0 && (
                <>
                  <div className="inv-detail__subhead">Verifikationer</div>
                  <ul className="inv-detail__rows">
                    {verifications.map((v) => {
                      const isAnnulled = v.reference_status === 'ANNULLED';
                      const isAnnulment = v.reference_type === 'verification_reversal';
                      return (
                        <li key={v.verification_id}>
                          <button
                            type="button"
                            className={`inv-detail__row${isAnnulled ? ' is-annulled' : ''}`}
                            onClick={() => handleViewVerification(v)}
                          >
                            <Receipt size={16} />
                            <span className="inv-detail__row-main">
                              <span className="inv-detail__row-title d-block">{v.series}{v.number}</span>
                              {(isAnnulled || isAnnulment) && (
                                <span className="inv-detail__row-sub">{isAnnulment ? 'Annullering' : 'Annullerad'}</span>
                              )}
                            </span>
                            <ChevronRight size={15} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {invoice.credit_notes?.length > 0 && (
                <>
                  <div className="inv-detail__subhead">Kreditnotor</div>
                  <ul className="inv-detail__rows">
                    {invoice.credit_notes.map((cn) => (
                      <li key={cn.id || cn.invoice_id}>
                        <button
                          type="button"
                          className="inv-detail__row"
                          onClick={() => history.push(`/${companyId}/edit-credit-note-new/${cn.id || cn.invoice_id}`)}
                        >
                          <FileMinus size={16} />
                          <span className="inv-detail__row-main">
                            <span className="inv-detail__row-title d-block">{cn.creditnote_number || 'Utkast'}</span>
                          </span>
                          {cn.total_sum ? (
                            <span className="inv-detail__row-amount">
                              −{formatCurrency(Math.abs(parseFloat(cn.total_sum)))}
                            </span>
                          ) : null}
                          <ChevronRight size={15} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Section>
          )}

          <div className="inv-detail__notes">
            <InvoiceNotesList invoiceId={invoiceId} />
          </div>
        </aside>
      </div>
      <RegisterPaymentModal
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        companyId={companyId}
        invoice={{ id: invoiceId, number: docTitle, customerName, invoiceDate: invoice.invoicedate }}
        remaining={isCreditNote && parseFloat(invoice.refund_amount || 0) > 0 ? parseFloat(invoice.refund_amount) : remainingAmount}
        isRefund={isCreditNote && parseFloat(invoice.refund_amount || 0) > 0}
        onRegistered={handlePaymentRegistered}
      />

      <EmailModal
        show={showEmailModal}
        handleClose={() => setShowEmailModal(false)}
        invoiceData={invoice}
        handleSendEmail={handleSendEmail}
      />

      {selectedVerification && (
        <VerificationDetailsModal
          showVerificationDetails={showVerificationDetails}
          setShowVerificationDetails={setShowVerificationDetails}
          selectedRow={selectedVerification}
          onVerificationUpdated={() => {}}
          viewOnly={true}
        />
      )}
    </>
  );
};

export default InvoiceDetailBootstrap;
